import { createFileRoute, Link } from "@tanstack/react-router";
import { Truck, Wallet, Gift, MessageCircle } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";
import { softCtaClass } from "@/components/ui/soft-cta";
import heroFallback from "@/assets/editorial-1.jpg?format=avif&quality=80";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/uae")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["uae.meta.title"] },
      { name: "description", content: DICTS.en["uae.meta.description"] },
      // صفحة هبوط إعلانيّة: لا تُفهرس لئلّا تنافس صفحات المتجر
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: DICTS.en["uae.meta.title"] },
      { property: "og:description", content: DICTS.en["uae.meta.description"] },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "preload", as: "image", href: heroFallback, fetchpriority: "high" }],
  }),
  component: UaeLandingPage,
});

const SECTION = "mx-auto w-full max-w-6xl px-4 sm:px-6";

function UaeLandingPage() {
  const { t } = useLocale();
  const waHref = `https://wa.me/${LECMO_WA_NUMBER}`;

  const perks = [
    { icon: Truck, key: "delivery" },
    { icon: Wallet, key: "cod" },
    { icon: Gift, key: "gift" },
    { icon: MessageCircle, key: "support" },
  ] as const;

  return (
    <SiteShell>
      <PageHero
        title={t("uae.hero.title")}
        subtitle={t("uae.hero.subtitle")}
        image={heroFallback}
        alt={t("uae.hero.alt")}
        pageKey="shop"
        hideBreadcrumbs
        cta={
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/shop" className={softCtaClass}>
              {t("uae.cta.shop")}
            </Link>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className={softCtaClass}
            >
              {t("uae.cta.whatsapp")}
            </a>
          </div>
        }
      />

      <section className={`${SECTION} py-14 sm:py-20`}>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map(({ icon: Icon, key }, i) => (
            <Reveal key={key} delayMs={i * 80}>
              <div className="flex flex-col gap-3">
                <Icon className="h-5 w-5 text-primary" aria-hidden />
                <h2 className="font-sans text-[12px] uppercase tracking-[0.18em] text-foreground">
                  {t(`uae.perks.${key}.title`)}
                </h2>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t(`uae.perks.${key}.body`)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className={`${SECTION} pb-20`}>
        <Reveal>
          <div className="border-t border-border pt-10 text-center">
            <h2 className="font-serif text-[clamp(1.6rem,4vw,2.4rem)] leading-tight text-foreground">
              {t("uae.closing.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
              {t("uae.closing.body")}
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link to="/shop" className={softCtaClass}>
                {t("uae.cta.shop")}
              </Link>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className={softCtaClass}
              >
                {t("uae.cta.whatsapp")}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </SiteShell>
  );
}
